import React, { useContext, useEffect, useState } from "react";
import useAssetTableJson from "../../../../../json/useAssetTableJson";

import useUrlParams from "../../../../../customHooks/useUrlParams";
import {
  AtsCompanyEdge,
  AtsCompanyOrderField,
  GetCompaniesResult,
  GetCompaniesVars,
} from "../../../customer/companies/queries/types";
import {
  GridAlignment,
  GridColDef,
  GridValidRowModel,
} from "@mui/x-data-grid-pro";
import {
  FETCH_MORE_DATA,
  ROWS_PER_PAGE,
  emptyMessage,
} from "../../../../../utils/constants";
import { useLazyQuery } from "@apollo/client";
import { GetCompanies } from "../../../customer/companies/queries/queries";
import { TableHeaderProps } from "../../../../../utils/types";
import { TABLE_ROW_HEIGHT } from "../../../../../styles/Variables";
import { Operation } from "../../../../../utils/Enum.types";
import { getModifiedScrollHeight } from "../../../../../utils/UtilFunctions";
import { OrderDirection } from "../../../../../utils/commonQueryTypes";
import useLabels from "../../../../../customHooks/useLabels";
import { StyledDatagrid } from "../../../../../styles/DatagridStyles";
import { Title } from "../../../../../stories/Title/Title";
import { Checkbox } from "@mui/material";
import useLoggedInUserDetails from "../../../../../customHooks/useLoggedInUserDetails";

interface Props {
  selectedIds: number[];
  handleCheckboxChange: (id: number) => void;
}

const CompaniesList = ({ selectedIds, handleCheckboxChange }: Props) => {
  const { UserRightsConfig } = useAssetTableJson();
 
  const { customer_id } = useUrlParams();
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [employees, setEmployees] = useState<AtsCompanyEdge[]>([]);
  const [rows, setRows] = useState<GridValidRowModel[]>([]);
  const [message, setMessage] = useState(emptyMessage);

  const { user_details } = useLoggedInUserDetails();

  const [searchData, setSearchData] = useState("");
  const [GetCompany, { data, loading, fetchMore }] = useLazyQuery<
    GetCompaniesResult,
    GetCompaniesVars
  >(GetCompanies);

  const dynamicHeaders: TableHeaderProps[] = UserRightsConfig.List.map(
    (header) => ({
      headerName: header.headerName,
      cellClassName: header.cellClassName,
      field: header.field,
      headerAlign: header.headerAlign as GridAlignment,
      align: header.align as GridAlignment,
      flex: header.flex,
      hideable: header.hideable,
      renderCell: (params) => {
        if (header.field === "checkbox") {
          return (
            <>
              <Checkbox
                value={params.row}
                checked={selectedIds.includes(params.row.company_id)}
                onChange={() => handleCheckboxChange(params.row.company_id)}
                inputProps={{ "aria-label": "controlled" }}
              />
            </>
          );
        }

        return params.value;
      },
    })
  );
  const columns: GridColDef[] = [...dynamicHeaders];

  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetCompanies.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newCompany) => {
          const filteredStudent = employees.find(
            (employee) => employee.node.id === newCompany.node.id
          );
          if (filteredStudent) {
            return {
              ...newCompany,
              node: {
                ...newCompany.node,
              },
            };
          }
          return newCompany;
        });
        setEmployees(updatedNewData);
        setRows(
          updatedNewData.map(({ node }, index) => ({
            id: index + 1,
            name: node.name,
            mobile: node.mobile,
            email: node.email,
            user_type: "",
            company_id: node.id,
          }))
        );
      } else {
        setEmployees(newData);
        setRows(
          newData.map(({ node }, index) => ({
            id: index + 1,
            name: node.name,
            mobile: node.mobile,
            email: node.email,
            user_type: "",
            company_id: node.id,
          }))
        );
      }
      setEndCursor(data.GetCompanies.pageInfo.endCursor);
    } // eslint-disable-next-line
  }, [data, loading]);

  useEffect(() => {
    const scrollTable = document.getElementsByClassName(
      "MuiDataGrid-virtualScroller"
    )[0] as Element;
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLDivElement;
      const scrollTop = target.scrollTop;
      const scrollHeight = target.scrollHeight;
      const clientHeight = target.clientHeight;
      if (scrollTop + clientHeight >= getModifiedScrollHeight(scrollHeight)) {
        if (hasNextPage && !loading) {
          fetchMore({
            variables: {
              first: FETCH_MORE_DATA,
              after: endCursor,
            },
            updateQuery: (prevResult, { fetchMoreResult }) => {
              if (!fetchMoreResult) return prevResult;

              const newEdges = fetchMoreResult.GetCompanies.edges;
              const pageInfo = fetchMoreResult.GetCompanies.pageInfo;
              setEndCursor(pageInfo.endCursor);
              setHasNextPage(pageInfo.hasNextPage);

              const duplicateCheck = prevResult.GetCompanies.edges.filter(
                ({ node: { id } }) =>
                  newEdges.findIndex(
                    ({ node: { id: newId } }) => newId === id
                  ) !== -1
              );

              if (duplicateCheck.length > 0) return prevResult;

              return {
                GetCompanies: {
                  edges: [...employees, ...newEdges],
                  pageInfo,
                  totalCount: data ? data.GetCompanies.totalCount! : 0,
                },
              };
            },
          });
        }
      }
    };
    if (scrollTable && rows.length)
      scrollTable.addEventListener("scroll", handleScroll);

    return () => {
      if (scrollTable) scrollTable.removeEventListener("scroll", handleScroll);
    };
  }, [rows]);

  useEffect(() => {
    if (customer_id) {
      GetCompany({
        variables: {
          after: null,
          customerID: customer_id,
          empID: user_details.id,
          first: ROWS_PER_PAGE,
          orderBy: {
            direction: OrderDirection.ASC,
            field: AtsCompanyOrderField.NAME,
          },
          
          name: searchData,
        },
      });
    }
  }, [customer_id, GetCompany, user_details.id, searchData]);

  const { companyLabel } = useLabels();

  return (
    <>
      <Title>{companyLabel}</Title>
      <StyledDatagrid
        columns={columns}
        rows={rows}
        rowHeight={TABLE_ROW_HEIGHT}
        disableRowSelectionOnClick
        hideFooter
      />
    </>
  );
};

export default CompaniesList;
